import { filter } from "lodash/fp";
import { useParams } from "react-router";
import { Navigate, useSearchParams } from "react-router-dom";

import ReportSubsection from "@app/analytics/components/reports/ReportSubsection";
import EmptyTab from "@app/analytics/MeetingReport/components/EmptyTab";
import { Moment } from "@app/analytics/MeetingReport/components/Moments/types";
import useMeetingReportSectionScroll from "@app/analytics/MeetingReport/hooks/useMeetingReportSectionScroll";
import useReportActions from "@app/analytics/MeetingReport/hooks/useReportActions";
import useSearch from "@app/analytics/MeetingReport/hooks/useSearch";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useTranscriptEditedToast from "@app/analytics/MeetingReport/hooks/useTranscriptEditedToast";
import useUserAttendance from "@app/analytics/MeetingReport/hooks/useUserAttendance";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import { CoachingWord } from "@app/analytics/MeetingReport/types";
import ROUTES from "@app/common/routes";
import { usePageTitle } from "@app/utils/hooks";

import { Column } from "@rui/Stack";

import BiasModule from "./BiasModule";
import CharismaModule from "./CharismaModule";
import EmptyCoachingContent from "./EmptyCoachingContent";
import FillerWordsModule from "./FillerWordsModule";
import InterruptionsModule from "./InterruptionsModule";
import MeetingReportCoachingSubNav, {
  CoachingSection,
} from "./MeetingReportCoachingSubNav";
import NonInclusiveTermsModule from "./NonInclusiveTermsModule";
import QuestionsAskedModule from "./QuestionsAskedModule";
import {
  Section,
  SectionHeading,
  SectionSubtitle,
  SectionTitle,
} from "./styles";
import TalkingPaceModule from "./TalkingPaceModule";

const MeetingReportCoachingContent = () => {
  usePageTitle("Meeting Report - Coaching");
  useTranscriptEditedToast();

  const { sessionId } = useParams();
  const [queryParams] = useSearchParams();
  const search = useSearch();

  const { data: sessionMetadata, isLoading } = useSessionMetadata();
  const { shouldJumpToTimestamp, makeJumpToTimestamp, makeScrollToItem } =
    useReportActions();

  const queriesEnabled =
    sessionMetadata.currentUserHasCoachingAccess &&
    !sessionMetadata.isRestrictedReport;

  const { wasInMeeting } = useUserAttendance({ enabled: queriesEnabled });
  const { turns } = useTranscript({ enabled: queriesEnabled });

  const searchTerm = useSearchStore((state) => state.searchTerm);

  const preselectedSection = queryParams.get("section") as CoachingSection;

  const [selectedSection, setSelectedSection] =
    useMeetingReportSectionScroll<CoachingSection>({
      defaultSection: "clarity",
      preselectedSection,
      sections: ["clarity", "inclusion", "impact"],
    });

  // redirect away if the user navigated straight to the coaching tab but doesn't have access
  if (!isLoading && !sessionMetadata.currentUserHasCoachingAccess) {
    return <Navigate to={ROUTES.meetingReportTabById(sessionId!, "recap")} />;
  }

  if (sessionMetadata.isRestrictedReport) {
    if (sessionMetadata.shouldUpsellTranscriptEntities) {
      return (
        <EmptyTab
          tab="Coaching"
          title="Become a better speaker"
          canUpgradeCurrentReport={
            sessionMetadata.currentUserCanUpgradeForReport
          }
        />
      );
    }

    return <EmptyCoachingContent type="no_data" />;
  }

  if (sessionMetadata.tier === "lite") {
    return <EmptyCoachingContent type="restricted_tier" />;
  }

  if (!wasInMeeting) {
    return <EmptyCoachingContent type="invalid_participant" />;
  }

  const handleToggleWordSearch = ({ word, turns }: CoachingWord) => {
    const wordIsAlreadySelected = word === searchTerm;
    if (wordIsAlreadySelected) {
      search.clearSearch();
    } else {
      search.performSearch(word, {
        appendQueryParam: false,
        exactPhrase: true,
        turns,
        searchNotes: false,
      });
    }
  };

  const handleClickWord = (word: CoachingWord) => {
    return () => handleToggleWordSearch(word);
  };

  const jumpToTimestamp = (moment: Moment) => {
    if (shouldJumpToTimestamp) {
      return makeJumpToTimestamp(moment.relativeStartTime, {
        onJumpTo: search.clearSearch,
      });
    }

    const momentTurns = filter({ id: moment.id }, turns);
    return makeScrollToItem({
      type: "paragraph",
      rawTimestamp: moment.startTime.toMillis(),
      turns: momentTurns,
      onScrollTo: search.clearSearch,
    });
  };

  return (
    <Column spacing={1}>
      <MeetingReportCoachingSubNav
        onSectionSelect={setSelectedSection}
        selectedSection={selectedSection}
      />
      <ReportSubsection id="clarity" sx={{ p: 0, pb: 1 }}>
        <Section>
          <SectionHeading>
            <SectionTitle>Clarity</SectionTitle>
            <SectionSubtitle>
              Measures how coherent your speech is
            </SectionSubtitle>
          </SectionHeading>
          <Column>
            <TalkingPaceModule onClickMoment={jumpToTimestamp} />
            <FillerWordsModule
              onClickMoment={jumpToTimestamp}
              onClickWord={handleClickWord}
            />
          </Column>
        </Section>
      </ReportSubsection>
      <ReportSubsection id="inclusion" sx={{ p: 0, pb: 1 }}>
        <Section>
          <SectionHeading>
            <SectionTitle>Inclusion</SectionTitle>
            <SectionSubtitle>
              Measures how equitable and appropriate your speech is
            </SectionSubtitle>
          </SectionHeading>
          <Column>
            <NonInclusiveTermsModule
              onClickMoment={jumpToTimestamp}
              onClickWord={handleClickWord}
            />
            <InterruptionsModule onClickMoment={jumpToTimestamp} />
          </Column>
        </Section>
      </ReportSubsection>
      <ReportSubsection id="impact" sx={{ p: 0, pb: 1 }}>
        <Section>
          <SectionHeading>
            <SectionTitle>Impact</SectionTitle>
            <SectionSubtitle>
              Measures how you are perceived, and how others perceive you
            </SectionSubtitle>
          </SectionHeading>
          <Column>
            <BiasModule />
            <CharismaModule />
            <QuestionsAskedModule onClickMoment={jumpToTimestamp} />
          </Column>
        </Section>
      </ReportSubsection>
    </Column>
  );
};

export default MeetingReportCoachingContent;
